import { useEffect, useMemo, useRef, useState } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Box from '@zenbusiness/zui/Box';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Radio from '@zenbusiness/zui/Radio';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import { InstanceDepositMoneyInput } from '#client/components/MoneyInputs';
import { formatMoneyDisplay } from '#client/components/MoneyInputs/utils/format';
import { formatCardHolder, formatLabelCardOption } from '#client/components/MoveMoney/utils/common';
import FieldReadView from '#client/components/ReadOrEdit/FieldReadView';
import { ACTIVE_CARD_LIMIT } from '#client/constants/astra';
import { DATE_FORMAT } from '#client/constants/date';
import useAstraProfileContext from '#client/hooks/useAstraProfileContext';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useFormStep from '#client/hooks/useFormStep';
import useInstantDepositLimit from '#client/hooks/useInstantDepositLimit';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import useOpenAstraModal from '#client/hooks/useOpenAstraModal';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccount from '#client/hooks/useUnitAccount';
import { capitalizeFirstLetter, getLast4digits } from '#client/utils/text';

import {
  DialogContentStyled,
  FieldContainerStyled,
  Wrapper,
  FormControlLabelStyled,
  RadioGroupStyled,
  LinkButtonStyled
} from './HowMuchForm.styles';

import { MODAL_ACTION } from '../constants/instants';

function CardOptions({
  defaultCardOption,
  handleOnChangeCard,
  selectedCard,
  listCardOptions,
  getAndRedirectAstraUrl,
  numberOfActiveCards
}) {
  const AddIcon = (
    <SharedAsset
      alt=""
      color="text.blue"
      src="/icon/misc/add-business.svg"
      width={16}
    />
  );

  return (
    <FieldContainerStyled>
      <RadioGroupStyled
        aria-label="selected-card"
        name="selected-card-radio-buttons-group"
        onChange={handleOnChangeCard}
        value={selectedCard ?? defaultCardOption.value?.uuid}
      >
        {listCardOptions.map(({ value, label, isNotAccepted, isCardExpired }) => (
          <Box key={value?.uuid}>
            <FormControlLabelStyled
              control={<Radio disabled={isNotAccepted || isCardExpired} />}
              label={
                <Stack>
                  {isNotAccepted && (
                    <Text
                      color="red"
                      size="xs"
                    >
                      Type not accepted
                    </Text>
                  )}
                  {isCardExpired && (
                    <Text
                      color="red"
                      size="xs"
                    >
                      Expired
                    </Text>
                  )}
                  <Text
                    color={isNotAccepted || isCardExpired ? 'tertiary' : 'primary'}
                    size="md"
                    weight="medium"
                  >
                    {label}
                  </Text>
                  <Text
                    color={isNotAccepted || isCardExpired ? 'tertiary' : 'primary'}
                    size="md"
                  >
                    {value.cardHolderName}
                  </Text>
                </Stack>
              }
              value={value?.uuid}
            />
          </Box>
        ))}
        {numberOfActiveCards < ACTIVE_CARD_LIMIT && (
          <Stack
            direction="row"
            justifyContent="flex-start"
          >
            <LinkButtonStyled
              color="info"
              onClick={() => getAndRedirectAstraUrl()}
              size="sm"
              variant="text"
            >
              <Stack
                direction="row"
                spacing="12px"
              >
                {AddIcon}
                <Text
                  color="blue"
                  size="md"
                >
                  Add debit card (3 max)
                </Text>
              </Stack>
            </LinkButtonStyled>
          </Stack>
        )}
      </RadioGroupStyled>
    </FieldContainerStyled>
  );
}

CardOptions.propTypes = {
  defaultCardOption: PropTypes.shape({
    value: PropTypes.shape({
      uuid: PropTypes.string,
      cardHolderName: PropTypes.string
    }),
    label: PropTypes.string
  }).isRequired,
  listCardOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.shape({
        uuid: PropTypes.string,
        cardHolderName: PropTypes.string
      }),
      label: PropTypes.string
    })
  ).isRequired,
  selectedCard: PropTypes.string,
  handleOnChangeCard: PropTypes.func.isRequired,
  getAndRedirectAstraUrl: PropTypes.func.isRequired,
  numberOfActiveCards: PropTypes.number
};

export default function HowMuchForm({ handleClose }) {
  const moneyInputRef = useRef(null);
  const { depositData, dispatchDepositData } = useFormStep();
  const { amount, selectedFromAccount } = depositData;
  const [moneyInputState, setMoneyInputState] = useState({
    value: amount,
    isError: false
  });

  const reviewButtonId = useEnterSubmit();
  const { createCommonErrorToast } = useToastContext();
  const {
    loading: astraProfileLoading,
    error: astraProfileError,
    cardList
  } = useAstraProfileContext();

  const {
    error: instantDepositLimitError,
    loading: instantDepositLimitLoading,
    hasLimit,
    limit: instantDepositLimit,
    remains: instantDepositRemain,
    transfers: instantDepositTransfers
  } = useInstantDepositLimit();
  const { dailyTransferCount, dailyTransferCountLimit } = instantDepositTransfers;
  const dailyTransferLimitRemaining = dailyTransferCountLimit - dailyTransferCount;

  const { getAndRedirectAstraUrl } = useOpenAstraModal();

  const { perTransferLimit, dailyTransferLimit, monthlyTransferLimit, minimumTransferLimit } =
    instantDepositLimit;
  const { dailyRemain, monthlyRemain } = instantDepositRemain;
  const { unitAccount, loading: loadingUnitAccount, error: errorUnitAccount } = useUnitAccount();
  const [selectedCard, setSelectedCard] = useState(selectedFromAccount?.id);

  const { accountNumber: toAccountNumber = '', depositProduct: toAccountType = '' } =
    unitAccount || {};
  const amountBalance = unitAccount?.availableBalance?.amount ?? {};

  const isLoading = astraProfileLoading || loadingUnitAccount || instantDepositLimitLoading;
  const isDisabledReviewTransfer = !!(
    isLoading ||
    !moneyInputState.value ||
    moneyInputState.isError ||
    errorUnitAccount ||
    astraProfileError ||
    !selectedCard
  );

  const expectedDate = useMemo(() => dayjs(new Date()).format(DATE_FORMAT.MOVE_MONEY), []);

  const filteredCardList = useMemo(
    () =>
      cardList
        ?.filter(
          (card) =>
            card.isSourceCard && !card.isInstantFundingCardRemoved && card.isCardValidForTransfers
        )
        .sort((a, b) => new Date(b.cardUpdatedAt) - new Date(a.cardUpdatedAt)),
    [cardList]
  );

  // Get list card returned from api
  const listCardOptions = useMemo(
    () =>
      (cardList ?? []).map(
        ({
          uuid,
          cardHolderFirstName,
          cardHolderLastName,
          cardType,
          cardLastFourDigits,
          isCardValidForTransfers,
          isCardExpired
        }) => ({
          value: {
            uuid,
            cardHolderName: formatCardHolder({ cardHolderFirstName, cardHolderLastName })
          },
          label: formatLabelCardOption({ cardType, lastFourDigits: cardLastFourDigits }),
          isNotAccepted: !isCardValidForTransfers,
          isCardExpired
        })
      ),
    [cardList]
  );

  // Get the default card (in the From section)
  const defaultCardOption = useMemo(() => {
    if (astraProfileLoading || !filteredCardList || filteredCardList?.length === 0) {
      return {};
    }

    const foundCard = (filteredCardList ?? []).find(
      ({ uuid }) => uuid === depositData?.selectedFromAccount?.uuid
    );

    // The default option is either chosen card before or the first card in the list card.
    const { uuid, cardHolderFirstName, cardHolderLastName, cardLastFourDigits, cardType } =
      foundCard ?? filteredCardList[0];

    return {
      value: {
        uuid,
        cardHolderName: formatCardHolder({ cardHolderFirstName, cardHolderLastName })
      },
      label: formatLabelCardOption({ cardType, lastFourDigits: cardLastFourDigits })
    };
  }, [astraProfileLoading, filteredCardList, depositData]);

  // Default option show on UI
  useEffect(() => {
    setSelectedCard(defaultCardOption?.value?.uuid);
  }, [defaultCardOption?.value?.uuid]);

  const buttonReviewTransferPressed = useButtonPressedCallback({
    elementName: 'review_deposit_instantdep',
    elementText: 'Review deposit',
    appPageName: 'Input_amount_Instantdep_modal'
  });

  const handleReviewTransfer = () => {
    buttonReviewTransferPressed();
    const isValid = moneyInputRef.current.validate();
    if (!isValid) {
      return;
    }

    const card = cardList.find(({ uuid }) => uuid === selectedCard);
    dispatchDepositData({
      type: MODAL_ACTION.GO_TO_IS_THIS_CORRECT_FORM,
      payload: {
        amount: moneyInputState.value,
        toAccount: unitAccount,
        selectedFromAccount: card,
        initDate: expectedDate
      }
    });
  };

  useEffect(() => {
    const orderErrors = astraProfileError || !!errorUnitAccount || instantDepositLimitError;
    const hasCard = !!cardList && cardList.length > 0;

    // If it has any error, close modal
    if (orderErrors) {
      handleClose();
      return;
    }

    // Check astra limit is existed or has list card
    if ((!astraProfileLoading && !hasCard) || (!instantDepositLimitLoading && !hasLimit)) {
      createCommonErrorToast();
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLimit, astraProfileLoading, errorUnitAccount, cardList, astraProfileError]);

  const handleOnChangeCard = (e) => {
    const { value } = e.target;
    setSelectedCard(value);
  };

  const buttonGetAndRedirectAstraUrlPressed = useButtonPressedCallback({
    elementName: 'add_debit_card',
    elementText: 'Add debit card (3 max)',
    appPageName: 'Input_amount_Instantdep_modal'
  });

  const handleGetAndRedirectAstraUrl = () => {
    buttonGetAndRedirectAstraUrlPressed();
    getAndRedirectAstraUrl();
  };

  useModalDisplayTracker({
    elementName: 'Input_amount_Instantdep_modal',
    elementText: 'Input_amount_Instantdep_modal',
    appPageName: 'active_banking_dashboard'
  });

  return (
    <>
      <DialogContentStyled role="form">
        <ContainerGrid>
          <FocusGrid>
            <Wrapper gap={{ xs: '32px', md: '40px' }}>
              <Heading
                size="3xl"
                weight="bold"
              >
                How much are you depositing?
              </Heading>
              <Stack gap="16px">
                <InstanceDepositMoneyInput
                  dailyLimit={dailyTransferLimit}
                  dailyRemainLimit={dailyRemain}
                  dailyTransferCountLimit={dailyTransferCountLimit}
                  dailyTransferLimitRemaining={dailyTransferLimitRemaining}
                  defaultValue={moneyInputState.value}
                  handleDataChange={setMoneyInputState}
                  minValuePerTransfer={minimumTransferLimit}
                  monthlyLimit={monthlyTransferLimit}
                  monthlyRemainLimit={monthlyRemain}
                  perTransferLimit={perTransferLimit}
                  ref={moneyInputRef}
                  title="Deposit Amount"
                />
              </Stack>
              <Stack spacing="8px">
                <Text
                  size="md"
                  weight="medium"
                >
                  From
                </Text>
                {astraProfileLoading ? (
                  <FieldContainerStyled>
                    <Stack
                      direction="column"
                      spacing="4px"
                    >
                      <Skeleton width="200px" />
                      <Skeleton width="200px" />
                    </Stack>
                  </FieldContainerStyled>
                ) : (
                  <CardOptions
                    defaultCardOption={defaultCardOption}
                    getAndRedirectAstraUrl={handleGetAndRedirectAstraUrl}
                    handleOnChangeCard={handleOnChangeCard}
                    listCardOptions={listCardOptions}
                    numberOfActiveCards={cardList.length}
                    selectedCard={selectedCard}
                  />
                )}
              </Stack>
              <Stack spacing="8px">
                <Text
                  size="md"
                  weight="medium"
                >
                  To
                </Text>
                <FieldContainerStyled>
                  {!toAccountType ? (
                    <Stack
                      direction="column"
                      spacing="4px"
                    >
                      <Skeleton width="200px" />
                      <Skeleton width="200px" />
                    </Stack>
                  ) : (
                    <FieldReadView
                      spacing={0}
                      title={`ZenBusiness Banking • ${formatMoneyDisplay(amountBalance / 100)}`}
                      value={`${capitalizeFirstLetter(toAccountType)} ${getLast4digits(toAccountNumber)}`}
                    />
                  )}
                </FieldContainerStyled>
              </Stack>
              <Stack spacing="8px">
                <Text
                  size="md"
                  weight="medium"
                >
                  Transfer type
                </Text>
                <FieldContainerStyled>
                  <FieldReadView
                    title="Instant Deposit"
                    value={`Expected ${expectedDate}`}
                  />
                </FieldContainerStyled>
              </Stack>
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionButtonsGrid>
        <LoadingButton
          color="primary"
          disabled={isDisabledReviewTransfer}
          fullWidth
          id={reviewButtonId}
          loading={isLoading}
          onClick={handleReviewTransfer}
          size="large"
          variant="contained"
        >
          Review deposit
        </LoadingButton>
      </ActionButtonsGrid>
    </>
  );
}

HowMuchForm.propTypes = {
  handleClose: PropTypes.func
};
